import { useCallback, useMemo } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Card, EmptyState, Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { LoadBoundary, useHubLoad } from "@/modules/hub-kit/HubKit";
import { API_VERSION_PREFIX } from "@/api/client";

interface MembershipSale {
  id: string;
  business_name: string;
  player_handle: string | null;
  player_name: string | null;
  format: "session_pack" | "period_pass" | "member_rate" | "subscription";
  amount: string;
  currency: string;
  channel: "pay_at_venue" | "desk";
  paid_at: string | null;
  created_at: string;
}

const FORMAT_LABEL: Record<MembershipSale["format"], string> = {
  session_pack: "Session pack",
  period_pass: "Period pass",
  member_rate: "Member rate",
  subscription: "Subscription",
};

/**
 * i257: every membership sale, read-only.
 *
 * A Pay at Venue sale appears once the business marks it paid; a desk sale
 * appears at once, with "At the desk". The amount is what HQ recorded, never a
 * client figure.
 */
export function FinanceMembershipsTab() {
  const client = useApiClient();
  const load = useCallback(async () => {
    const body = await client.get<{ sales: MembershipSale[] }>(
      `${API_VERSION_PREFIX}/hq/memberships/sales?limit=100`,
    );
    return body.sales ?? [];
  }, [client]);
  const { data, error, loading, reload } = useHubLoad(load);

  const total = useMemo(
    () => (data ?? []).reduce((sum, s) => sum + Number(s.amount), 0),
    [data],
  );

  return (
    <Stack gap={16}>
      <LoadBoundary
        loading={loading}
        error={error}
        onRetry={() => void reload()}
        hasData={!!data}
        what="membership sales"
      >
        {data ? (
          <Card
            header={
              <Text role="title" as="h3">
                Membership sales ({data.length})
              </Text>
            }
          >
            {data.length === 0 ? (
              <EmptyState title="No membership sales yet" />
            ) : (
              <Stack gap={12}>
                <Text role="body" color="muted" as="p">
                  {data.length} sale{data.length === 1 ? "" : "s"}, ₱{total.toLocaleString()} recorded.
                </Text>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Business</Th>
                      <Th>Player</Th>
                      <Th>Format</Th>
                      <Th>Amount</Th>
                      <Th>Sold</Th>
                      <Th>Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((s) => (
                      <Tr key={s.id}>
                        <Td>{s.business_name}</Td>
                        <Td>{s.player_handle ? `@${s.player_handle}` : s.player_name ?? "-"}</Td>
                        <Td>{FORMAT_LABEL[s.format]}</Td>
                        <Td>₱{Number(s.amount).toLocaleString()}</Td>
                        <Td>{s.channel === "desk" ? "At the desk" : "Pay at Venue"}</Td>
                        <Td>{new Date(s.paid_at ?? s.created_at).toLocaleDateString()}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Stack>
            )}
          </Card>
        ) : null}
      </LoadBoundary>
    </Stack>
  );
}
